import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Shield, Clock, Star, Phone, Instagram, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { servicesData, testimonials, contactInfo } from '../utils/mockData';

const Home = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-cyan-50 via-emerald-50 to-white py-12 lg:py-8 lg:pt-4 overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        
        {/* Click-to-Call Phone Number - Desktop & Mobile */}
        <div className="container mx-auto px-4 relative z-10">
          <div className="flex flex-col sm:flex-row justify-between items-start gap-2 mb-8">
            {/* Instagram Link - Top Left */}
            <a 
              href="https://instagram.com/tomthewindowcleaner" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center justify-center space-x-2 bg-white px-4 py-2 rounded-full shadow-md hover:shadow-lg transition-shadow border border-cyan-200 hover:border-cyan-400 w-full sm:w-auto"
            >
              <Instagram className="w-4 h-4 text-purple-600 flex-shrink-0" />
              <span className="text-cyan-600 font-semibold text-sm whitespace-nowrap">Follow us on Instagram</span>
            </a>
            
            {/* Phone Number - Top Right */}
            <a 
              href="tel:07583331372" 
              className="flex items-center justify-center space-x-2 bg-white px-4 py-2 rounded-full shadow-md hover:shadow-lg transition-shadow border border-cyan-200 hover:border-cyan-400 w-full sm:w-auto"
            >
              <Phone className="w-4 h-4 text-cyan-600 flex-shrink-0" />
              <span className="text-cyan-600 font-semibold text-sm whitespace-nowrap">{contactInfo.phone}</span>
            </a>
          </div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-flex flex-wrap items-center justify-center sm:justify-start">
                <span className="px-3 sm:px-6 py-2 sm:py-3 bg-cyan-100 text-cyan-700 rounded-full text-xs sm:text-base lg:text-lg font-semibold flex items-center gap-2">
                  <span className="flex gap-1 text-sm sm:text-base">⭐⭐⭐⭐⭐</span>
                  <span className="whitespace-nowrap">Professional External Cleaning Services</span>
                </span>
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                Professional Results,
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-emerald-600"> Every Time</span>
              </h1>
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 shadow-md max-w-2xl mx-auto lg:mx-0 mb-6">
                <p className="font-semibold text-gray-900 mb-3">Services We Offer:</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                    <span className="text-gray-700">Window Cleaning</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                    <span className="text-gray-700">Gutter Cleaning</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                    <span className="text-gray-700">Gutter Clearing</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                    <span className="text-gray-700">Pressure Washing</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                    <span className="text-gray-700">Soft Washing</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                    <span className="text-gray-700">Solar Panel Cleaning</span>
                  </div>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button size="lg" className="bg-gradient-to-r from-cyan-500 to-emerald-500 hover:from-cyan-600 hover:to-emerald-600 text-white group">
                    Get a Free Quote
                    <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/estimates">
                  <Button size="lg" variant="outline" className="border-2 border-cyan-500 text-cyan-600 hover:bg-cyan-50">
                    View Pricing
                  </Button>
                </Link>
              </div>
              <div className="flex items-center space-x-6 pt-4">
                <div className="flex items-center space-x-2">
                  <Shield className="w-5 h-5 text-emerald-600" />
                  <span className="text-sm text-gray-600">Fully Insured</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Star className="w-5 h-5 text-yellow-500" />
                  <span className="text-sm text-gray-600">5-Star Rated</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-300">
                <img
                  src="https://customer-assets.emergentagent.com/job_crystal-clean-3/artifacts/sxekq2lu_IMG_1208.jpg"
                  alt="Professional window cleaning"
                  className="w-full h-[500px] object-cover"
                  style={{ objectPosition: 'center 60%' }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-6 border border-gray-100">
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <div className="w-5 h-5 rounded-full bg-gradient-to-br from-cyan-500 to-emerald-500 flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-xs">✓</span>
                    </div>
                    <p className="text-sm font-bold text-gray-900">Window cleaning since 2003</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-5 h-5 rounded-full bg-gradient-to-br from-cyan-500 to-emerald-500 flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-xs">✓</span>
                    </div>
                    <p className="text-sm font-bold text-gray-900">Trusted by the same customers for over 20 years</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-5 h-5 rounded-full bg-gradient-to-br from-cyan-500 to-emerald-500 flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-xs">✓</span>
                    </div>
                    <p className="text-sm font-bold text-gray-900">Less than 1% call back rate</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-5 h-5 rounded-full bg-gradient-to-br from-cyan-500 to-emerald-500 flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-xs">✓</span>
                    </div>
                    <p className="text-sm font-bold text-gray-900">No payments upfront</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Why Choose Us?</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Professional, reliable, and trusted by hundreds of customers across Neath and surrounding areas.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-2 border-gray-100 hover:border-cyan-200 hover:shadow-lg transition-all duration-300">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-cyan-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Fully Insured</h3>
                <p className="text-gray-600">
                  Complete peace of mind with full Public Liability insurance.
                </p>
              </CardContent>
            </Card>
            <Card className="border-2 border-gray-100 hover:border-emerald-200 hover:shadow-lg transition-all duration-300">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Reliable & Punctual</h3>
                <p className="text-gray-600">
                  We arrive on time and complete the job to the highest standard.
                </p>
              </CardContent>
            </Card>
            <Card className="border-2 border-gray-100 hover:border-cyan-200 hover:shadow-lg transition-all duration-300">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-8 h-8 text-cyan-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Spotless Results</h3>
                <p className="text-gray-600">
                  Using purified water systems for streak-free, crystal clear windows.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Our Services</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive cleaning solutions for your home or business.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {servicesData.slice(0, 3).map((service) => (
              <Card key={service.id} className="overflow-hidden border-2 border-gray-100 hover:border-cyan-200 hover:shadow-xl transition-all duration-300 group">
                {service.beforeAfterImages ? (
                  <div className="relative h-56 overflow-hidden grid grid-cols-2 gap-1">
                    <div className="relative overflow-hidden">
                      <img
                        src={service.beforeAfterImages[0]}
                        alt={`${service.title} - Before`}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    </div>
                    <div className="relative overflow-hidden">
                      <img
                        src={service.beforeAfterImages[1]}
                        alt={`${service.title} - After`}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    </div>
                    <h3 className="absolute bottom-4 left-4 text-2xl font-bold text-white">{service.title}</h3>
                  </div>
                ) : (
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      style={
                        service.title === 'Gutter Cleaning' ? { objectPosition: 'center 20%' } :
                        service.title === 'Window Cleaning' ? { objectPosition: 'center 10%' } : {}
                      }
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    <h3 className="absolute bottom-4 left-4 text-2xl font-bold text-white">{service.title}</h3>
                  </div>
                )}
                <CardContent className="p-6">
                  <p className="text-gray-600 mb-4">{service.description}</p>
                  <Link to={`/services#service-${service.id}`}>
                    <Button variant="link" className="text-cyan-600 p-0 h-auto font-semibold group/btn">
                      Learn More
                      <ArrowRight className="ml-1 w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
          
          {/* Centered row for remaining 2 services */}
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto mt-8">
            {servicesData.slice(3, 5).map((service) => (
              <Card key={service.id} className="overflow-hidden border-2 border-gray-100 hover:border-cyan-200 hover:shadow-xl transition-all duration-300 group">
                {service.beforeAfterImages ? (
                  <div className="relative h-56 overflow-hidden grid grid-cols-2 gap-1">
                    <div className="relative overflow-hidden">
                      <img
                        src={service.beforeAfterImages[0]}
                        alt={`${service.title} - Before`}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                    <div className="relative overflow-hidden">
                      <img
                        src={service.beforeAfterImages[1]}
                        alt={`${service.title} - After`}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    <h3 className="absolute bottom-4 left-4 text-2xl font-bold text-white">{service.title}</h3>
                  </div>
                ) : (
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    <h3 className="absolute bottom-4 left-4 text-2xl font-bold text-white">{service.title}</h3>
                  </div>
                )}
                <CardContent className="p-6">
                  <p className="text-gray-600 mb-4">{service.description}</p>
                  <Link to={`/services#service-${service.id}`}>
                    <Button variant="link" className="text-cyan-600 p-0 h-auto font-semibold group/btn">
                      Learn More
                      <ArrowRight className="ml-1 w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <div className="text-center mt-8">
            <Link to="/services">
              <Button size="lg" variant="outline" className="border-2 border-cyan-500 text-cyan-600 hover:bg-cyan-50">
                View All Services
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">What Our Customers Say</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Don't just take our word for it - hear from our satisfied customers.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-2 border-gray-100 hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-4 italic">"{testimonial.text}"</p>
                  <div className="border-t pt-4">
                    <p className="font-semibold text-gray-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-500">{testimonial.location}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-cyan-600 to-emerald-600">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
            Ready for Sparkling Clean Windows?
          </h2>
          <p className="text-xl text-cyan-50 mb-8 max-w-2xl mx-auto">
            Get your free quote today and experience the difference professional window cleaning makes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-cyan-600 hover:bg-gray-100">
                Contact Us Now
              </Button>
            </Link>
            <Link to="/estimates">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10">
                View Our Prices
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
